import React, { useState, useEffect, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import {
  Menu,
  X,
  Sun,
  Moon,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Brain,
  Sparkles,
  BarChart3,
  Cloud,
  Shield,
  Code2,
  Settings,
  Cpu,
  Landmark,
  HeartPulse,
  ShoppingCart,
  Zap,
  Factory,
  Radio,
  Building2,
  Monitor,
} from "lucide-react"
import { Link } from "gatsby"
import { useLocation } from "@reach/router"
import { useTheme } from "next-themes"

// ─── Data ────────────────────────────────────────────────────────────────────

const services = [
  {
    label: "AI Strategy & Consulting",
    href: "/services/ai-strategy-consulting",
    icon: Brain,
    description: "End-to-end AI roadmap and implementation planning",
  },
  {
    label: "Generative AI Solutions",
    href: "/services/generative-ai",
    icon: Sparkles,
    description: "Custom LLM, copilot, and content-generation systems",
  },
  {
    label: "Data & Analytics",
    href: "/services/data-analytics",
    icon: BarChart3,
    description: "Data platforms, BI, and advanced analytics",
  },
  {
    label: "Cloud Transformation",
    href: "/services/cloud-transformation",
    icon: Cloud,
    description: "Cloud migration, modernization, and optimization",
  },
  {
    label: "Cybersecurity",
    href: "/services/cybersecurity",
    icon: Shield,
    description: "Threat detection, compliance, and zero-trust architecture",
  },
  {
    label: "Digital Engineering",
    href: "/services/digital-engineering",
    icon: Code2,
    description: "Full-stack product development and platform engineering",
  },
  {
    label: "Managed AI Services",
    href: "/services/managed-ai",
    icon: Settings,
    description: "24/7 monitoring, MLOps, and model lifecycle management",
  },
  {
    label: "Technology Transformation",
    href: "/services/technology-transformation",
    icon: Cpu,
    description: "Legacy modernization and enterprise architecture",
  },
]

const industries = [
  {
    label: "Banking & Financial Services",
    href: "/industries/banking-financial-services",
    icon: Landmark,
  },
  {
    label: "Healthcare & Life Sciences",
    href: "/industries/healthcare-life-sciences",
    icon: HeartPulse,
  },
  {
    label: "Retail & Consumer Goods",
    href: "/industries/retail-consumer-goods",
    icon: ShoppingCart,
  },
  {
    label: "Energy & Utilities",
    href: "/industries/energy-utilities",
    icon: Zap,
  },
  {
    label: "Manufacturing",
    href: "/industries/manufacturing",
    icon: Factory,
  },
  {
    label: "Communications & Media",
    href: "/industries/communications-media",
    icon: Radio,
  },
  {
    label: "Public Sector",
    href: "/industries/public-sector",
    icon: Building2,
  },
  {
    label: "Technology",
    href: "/industries/technology",
    icon: Monitor,
  },
]

const navItems = [
  { label: "What We Do", href: "#", hasMegaMenu: true },
  { label: "Case Studies", href: "/case-studies", hasMegaMenu: false },
  { label: "Insights", href: "/blog", hasMegaMenu: false },
  { label: "About", href: "/about", hasMegaMenu: false },
  { label: "Careers", href: "/careers", hasMegaMenu: false },
]

// ─── Component ───────────────────────────────────────────────────────────────

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false)
  const [megaMenuOpen, setMegaMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  const megaMenuTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const megaMenuRef = useRef<HTMLDivElement>(null)

  const location = useLocation()
  const pathname = location.pathname
  const { resolvedTheme, setTheme } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setMobileSubmenuOpen(false)
    setMegaMenuOpen(false)
  }, [pathname])

  // Close mega menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMegaMenuOpen(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const isActiveLink = useCallback(
    (href: string) => {
      if (href === "/" || href === "#") return false
      return pathname.startsWith(href)
    },
    [pathname]
  )

  const isWhatWeDoActive =
    pathname.startsWith("/services") || pathname.startsWith("/industries")

  // ── Mega menu hover handlers (desktop) ──

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeout.current) {
      clearTimeout(megaMenuTimeout.current)
      megaMenuTimeout.current = null
    }
    setMegaMenuOpen(true)
  }

  const handleMegaMenuLeave = () => {
    megaMenuTimeout.current = setTimeout(() => {
      setMegaMenuOpen(false)
    }, 200)
  }

  // ── Theme toggle ──

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark")
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="relative max-w-7xl mx-auto">
        {/* ─── Main bar ─── */}
        <div className="flex h-16 items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="font-bold text-xl text-foreground tracking-tight">
              Shailka
            </span>
          </Link>

          {/* ─── Desktop Navigation ─── */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) =>
              item.hasMegaMenu ? (
                /* "What We Do" trigger with mega menu */
                <div
                  key={item.label}
                  ref={megaMenuRef}
                  className="relative"
                  onMouseEnter={handleMegaMenuEnter}
                  onMouseLeave={handleMegaMenuLeave}
                >
                  <button
                    className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      isWhatWeDoActive || megaMenuOpen
                        ? "text-primary bg-primary/5"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                    onClick={() => setMegaMenuOpen((prev) => !prev)}
                    aria-expanded={megaMenuOpen}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        megaMenuOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              ) : (
                /* Standard nav links */
                <Link
                  key={item.label}
                  to={item.href}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActiveLink(item.href)
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {item.label}
                  {isActiveLink(item.href) && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              )
            )}
          </div>

          {/* ─── Right side (desktop) ─── */}
          <div className="hidden lg:flex items-center gap-2">
            {mounted && (
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
                aria-label="Toggle theme"
              >
                {resolvedTheme === "dark" ? (
                  <Sun size={18} />
                ) : (
                  <Moon size={18} />
                )}
              </button>
            )}

            <Link to="/contact">
              <Button
                size="sm"
                className="bg-gradient-primary text-white border-0 shadow-md hover:shadow-lg transition-shadow duration-200"
              >
                Contact Us
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* ─── Mobile controls ─── */}
          <div className="lg:hidden flex items-center gap-1">
            {mounted && (
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {resolvedTheme === "dark" ? (
                  <Sun size={20} />
                ) : (
                  <Moon size={20} />
                )}
              </button>
            )}
            <button
              className="p-2 text-foreground"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* ─── Desktop Mega Menu Dropdown ─── */}
        <div
          onMouseEnter={handleMegaMenuEnter}
          onMouseLeave={handleMegaMenuLeave}
          className={`hidden lg:block absolute left-0 right-0 top-full transition-all duration-300 ease-in-out ${
            megaMenuOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-2 pointer-events-none"
          }`}
        >
          <div className="bg-background/95 backdrop-blur-2xl border-b border-border/50 shadow-2xl">
            <div className="max-w-7xl mx-auto px-6 py-8">
              <div className="grid grid-cols-2 gap-10">
                {/* ── Services Column ── */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4 px-3">
                    Services
                  </h3>
                  <div className="grid grid-cols-1 gap-0.5">
                    {services.map((service) => {
                      const Icon = service.icon
                      return (
                        <Link
                          key={service.label}
                          to={service.href}
                          className="group flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors duration-150"
                        >
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors duration-150 group-hover:bg-primary group-hover:text-white">
                            <Icon size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-150">
                              {service.label}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {service.description}
                            </p>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>

                {/* ── Industries Column ── */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4 px-3">
                    Industries
                  </h3>
                  <div className="grid grid-cols-1 gap-0.5">
                    {industries.map((industry) => {
                      const Icon = industry.icon
                      return (
                        <Link
                          key={industry.label}
                          to={industry.href}
                          className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors duration-150"
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors duration-150 group-hover:bg-primary group-hover:text-white">
                            <Icon size={16} />
                          </div>
                          <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-150">
                            {industry.label}
                          </span>
                        </Link>
                      )
                    })}
                  </div>

                  {/* CTA inside mega menu */}
                  <div className="mt-6 px-3">
                    <Link
                      to="/contact"
                      className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                    >
                      Talk to an expert
                      <ChevronRight
                        size={14}
                        className="transition-transform duration-200 group-hover:translate-x-0.5"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Mobile Menu ─── */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="border-t border-border/50 py-4 px-6 bg-background/95 backdrop-blur-xl">
            <div className="flex flex-col gap-1">
              {/* "What We Do" accordion */}
              <div>
                <button
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isWhatWeDoActive || mobileSubmenuOpen
                      ? "text-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                  onClick={() => setMobileSubmenuOpen((prev) => !prev)}
                  aria-expanded={mobileSubmenuOpen}
                >
                  What We Do
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${
                      mobileSubmenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    mobileSubmenuOpen
                      ? "max-h-[600px] opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="pl-4 pt-2 pb-1">
                    {/* Services */}
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 px-3">
                      Services
                    </p>
                    {services.map((service) => {
                      const Icon = service.icon
                      return (
                        <Link
                          key={service.label}
                          to={service.href}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon size={14} className="text-primary shrink-0" />
                          {service.label}
                        </Link>
                      )
                    })}

                    {/* Industries */}
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-2 px-3">
                      Industries
                    </p>
                    {industries.map((industry) => {
                      const Icon = industry.icon
                      return (
                        <Link
                          key={industry.label}
                          to={industry.href}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon size={14} className="text-primary shrink-0" />
                          {industry.label}
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Remaining nav links */}
              {navItems
                .filter((item) => !item.hasMegaMenu)
                .map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink(item.href)
                        ? "text-primary bg-primary/5"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}

              {/* Mobile CTA */}
              <div className="pt-4 mt-2 border-t border-border/50">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    size="sm"
                    className="w-full bg-gradient-primary text-white border-0"
                  >
                    Contact Us
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
